import React, { useEffect, useRef, useState } from 'react';
import { demeterArtificialIntelligenceApi } from '../../../Apis/Apis';
import { GetDemeterArtificialIntelligencePromptResponse, UpdateDemeterArtificialIntelligencePromptRequest } from '../../../Generated/Raven-Demeter';
import useApiWithoutAutoExecute from '../../Apis/Hooks/useApiWithoutAutoExecute';
import LinkButton, { LinkButtonType } from '../../Components/Form/Buttons/LinkButton';
import CardSkeleton from '../../Components/Layout/CardSkeleton';
import useLanguage from '../../Services/Language/useLanguageHook';
import styles from './ArtificialIntelligence.module.scss';

interface IPromptEditorProps {
    promptGuid: string;
    onBack: () => void;
}

const PromptEditor: React.FC<IPromptEditorProps> = (props) => {
    const [translations] = useLanguage();
    const [content, setContent] = useState('');

    const [loading, refresh, getResponse, error] = useApiWithoutAutoExecute<void, GetDemeterArtificialIntelligencePromptResponse>(
        () => demeterArtificialIntelligenceApi.getPrompt(props.promptGuid, { params: { RefreshCache: true } }),
        { errorMessage: 'Failed to load prompt', showResponseErrorMessage: true },
    );

    const [saving, savePrompt] = useApiWithoutAutoExecute<UpdateDemeterArtificialIntelligencePromptRequest, object>(
        (request) => {
            if (!request) {
                return null;
            }
            return demeterArtificialIntelligenceApi.updatePrompt(request.demeterArtificialIntelligencePromptGuid, request);
        },
        { successMessage: 'Prompt saved', errorMessage: 'Failed to save prompt' },
    );

    useEffect(() => {
        refresh();
    }, [props.promptGuid]);

    useEffect(() => {
        if (getResponse?.prompt?.content) {
            setContent(getResponse.prompt.content);
        }
    }, [getResponse?.prompt?.content]);

    const savedContent = getResponse?.prompt?.content ?? '';
    const isDirty = content !== savedContent;

    const handleSave = () => {
        savePrompt({
            demeterArtificialIntelligencePromptGuid: props.promptGuid,
            content,
            promptType: getResponse?.prompt?.promptType,
        });
    };

    const previousSavingReference = useRef(false);

    useEffect(() => {
        if (previousSavingReference.current && !saving) {
            refresh();
        }
        previousSavingReference.current = saving;
    });

    return (
        <div className={styles.page_container} data-testid="PromptEditor">
            <div className={styles.prompt_editor_header}>
                <LinkButton
                    title={translations.actions.back}
                    type={LinkButtonType.White}
                    onClick={props.onBack}
                    testId="PromptEditorBackButton"
                    track={{ eventCategory: 'AiNewsRating', eventAction: 'PromptEditorBack' }}
                />
                <LinkButton
                    title={saving ? '...' : translations.actions.save}
                    type={LinkButtonType.Blue}
                    onClick={handleSave}
                    disabled={saving || !isDirty}
                    testId="PromptEditorSaveButton"
                    track={{ eventCategory: 'AiNewsRating', eventAction: 'PromptEditorSave' }}
                />
            </div>

            {loading && <CardSkeleton minimumHeight={300} />}
            {error && <div className={styles.error_message}>{error}</div>}

            {!loading && !error && (
                <textarea
                    className={styles.prompt_editor_textarea}
                    value={content}
                    onChange={(event) => setContent(event.target.value)}
                    disabled={saving}
                    data-testid="PromptEditorTextarea"
                    spellCheck={false}
                />
            )}
        </div>
    );
};

export default PromptEditor;
